import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Crown, Check, Sparkles, ArrowLeft, Loader2, ShieldCheck } from 'lucide-react';

import { QuoteLayout } from '@/components/quotes/QuoteLayout';
import { useQuoteEntitlement } from '@/hooks/useQuoteEntitlement';
import { Button } from '@/components/ui/button';
import { getPaddle } from '@/lib/paddle';
import { trackBeginCheckout, trackPurchase } from '@/lib/analytics';

const API_URL = process.env.REACT_APP_API_URL;

/**
 * Localised money formatter. Uses tr-TR for TRY-based plans and en-US
 * for USD-based plans so the number layout feels native. Falls back to
 * a plain string if Intl isn't available.
 */
function fmtMoney(amount, currency = 'TL', lang = 'tr') {
  const n = Number(amount || 0);
  const localeMap = { tr: 'tr-TR', en: 'en-US', fr: 'fr-FR', de: 'de-DE', es: 'es-ES' };
  const locale = localeMap[lang] || (currency === 'USD' ? 'en-US' : 'tr-TR');
  const symbol = currency === 'USD' ? '$' : (currency === 'TRY' ? '₺' : currency);
  const nStr = n.toLocaleString(locale, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  if (currency === 'USD') return `${symbol}${nStr}`;
  return `${nStr} ${symbol}`;
}

/**
 * QuoteUpgrade
 * ------------
 * Free/Pro comparison + PayTR iframe checkout for the Quote Builder.
 *
 * • Prices come from GET /api/quote/billing/plans (backend-authoritative).
 *   The backend selects the provider from the tenant's stored billing country:
 *   Türkiye => PayTR/TRY, other countries => Paddle/USD. Interface language
 *   does not decide the payment provider.
 * • Türkiye plan selection posts to the PayTR checkout endpoint. International
 *   billing uses Paddle.js with server-tracked checkout refs.
 * • Renewal wording explicitly states there is NO auto-renewal.
 */
export const QuoteUpgrade = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('quotes');
  const { entitlement, isPro, loading: entLoading, refresh: refreshEnt } = useQuoteEntitlement();

  const [plans, setPlans] = useState(null);
  const [period, setPeriod] = useState('yearly'); // "monthly" | "yearly"
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [iframeUrl, setIframeUrl] = useState(null);
  const [pollingSession, setPollingSession] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/quote/billing/plans`);
        setPlans(data);
      } catch (err) {
        toast.error(err.response?.data?.detail || t('upgrade.loadPlansError'));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // React to redirect-back from PayTR / Paddle. Restore the tracked checkout
  // session so purchase is emitted only after the backend confirms "paid".
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const st = params.get('status');
    const checkoutRef = params.get('checkout_ref');
    let storedSession = '';
    try { storedSession = window.localStorage.getItem('paydaai_pending_checkout_session') || ''; } catch { /* noop */ }

    if (st === 'success') {
      toast.success(t('upgrade.successBanner'));
      if (storedSession) setPollingSession(storedSession);
      refreshEnt();
    } else if (st === 'paddle_pending') {
      if (checkoutRef || storedSession) setPollingSession(checkoutRef || storedSession);
    } else if (st === 'fail') {
      toast.error(t('upgrade.failBanner'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Poll for post-webhook completion when iframe is open
  useEffect(() => {
    if (!pollingSession) return undefined;
    let cancelled = false;
    let tries = 0;
    const tick = async () => {
      if (cancelled) return;
      tries += 1;
      try {
        const { data } = await axios.get(`${API_URL}/api/quote/billing/status/${pollingSession}`);
        if (data.payment_status === 'paid') {
          const provider = String(pollingSession).startsWith('paddle_') ? 'paddle' : 'paytr';
          trackPurchase({
            sessionId: pollingSession,
            provider,
            plan: data.plan,
            amount: data.amount,
            currency: data.currency,
          });
          try {
            window.localStorage.removeItem('paydaai_pending_checkout_session');
            window.localStorage.removeItem('paydaai_pending_checkout_meta');
          } catch { /* noop */ }
          toast.success(t('upgrade.proActivated'));
          setIframeUrl(null);
          setPollingSession(null);
          refreshEnt();
          // Re-read plans so a new Paddle subscriber immediately sees
          // "Manage subscription" instead of another checkout button.
          axios.get(`${API_URL}/api/quote/billing/plans`)
            .then(({ data: fresh }) => { if (!cancelled) setPlans(fresh); })
            .catch(() => { /* noop */ });
          return;
        }
      } catch {
        /* keep polling — the record must exist */
      }
      if (tries < 60 && !cancelled) {
        setTimeout(tick, 3000);
      }
    };
    tick();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollingSession, refreshEnt]);

  const buy = async (planKey) => {
    // Second guard: if the backend says USD is disabled, don't even try —
    // show the polite English message from the brief. The backend will
    // also refuse with 503, but this avoids an extra round-trip.
    setPurchasing(true);
    try {
      if (plans?.provider === 'paddle') {
        const { data } = await axios.post(`${API_URL}/api/quote/billing/paddle-checkout`, {
          plan: planKey,
        });
        const Paddle = await getPaddle();
        setPollingSession(data.checkout_ref);
        try {
          window.localStorage.setItem('paydaai_pending_checkout_session', data.checkout_ref);
          window.localStorage.setItem('paydaai_pending_checkout_meta', JSON.stringify({ provider: 'paddle', plan: planKey }));
        } catch { /* noop */ }
        trackBeginCheckout({
          provider: 'paddle',
          plan: planKey,
          amount: data.amount,
          currency: data.currency,
          sessionId: data.checkout_ref,
        });
        Paddle.Checkout.open({
          items: [{ priceId: data.price_id, quantity: 1 }],
          customer: data.customer_email ? { email: data.customer_email } : undefined,
          customData: { checkout_ref: data.checkout_ref },
          settings: {
            displayMode: 'overlay',
            theme: 'light',
            locale: ['fr', 'de', 'es', 'en'].includes(plans?.language) ? plans.language : 'en',
            showAddDiscounts: true,
            successUrl: `${window.location.origin}/quote-upgrade?status=paddle_pending&checkout_ref=${encodeURIComponent(data.checkout_ref)}`,
          },
        });
        return;
      }

      const { data } = await axios.post(`${API_URL}/api/quote/billing/checkout`, {
        plan: planKey, source: 'paytr',
      });
      setIframeUrl(data.url);
      setPollingSession(data.session_id);
      try {
        window.localStorage.setItem('paydaai_pending_checkout_session', data.session_id);
        window.localStorage.setItem('paydaai_pending_checkout_meta', JSON.stringify({ provider: 'paytr', plan: planKey }));
      } catch { /* noop */ }
      trackBeginCheckout({
        provider: 'paytr',
        plan: planKey,
        amount: data.amount,
        currency: data.currency,
        sessionId: data.session_id,
      });
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Backend USD gate: reason: "usd_not_enabled"
      if (
        err.response?.status === 503
        && typeof detail === 'object'
        && detail?.reason === 'usd_not_enabled'
      ) {
        toast.error(t('upgrade.usdDisabled'));
        return;
      }
      const msg = typeof detail === 'string' ? detail : (detail?.message || t('upgrade.checkoutError'));
      toast.error(msg);
    } finally {
      setPurchasing(false);
    }
  };

  const [openingPortal, setOpeningPortal] = useState(false);
  const openPortal = async (target = 'overview') => {
    // Open the tab synchronously (inside the click) so popup blockers allow
    // it, then point it at the authenticated Paddle portal link.
    const portalWindow = window.open('', '_blank');
    setOpeningPortal(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/quote/billing/paddle-portal`);
      const url = (target === 'payment' && data.update_payment_method_url)
        || data.overview_url || data.view_subscription_url;
      if (!url) throw new Error('missing portal url');
      if (portalWindow) {
        portalWindow.opener = null;
        portalWindow.location.href = url;
      } else {
        window.location.href = url;
      }
    } catch {
      if (portalWindow) portalWindow.close();
      toast.error(t('upgrade.portalError'));
    } finally {
      setOpeningPortal(false);
    }
  };

  // Monthly <-> yearly switch for an existing Paddle subscription: preview
  // what Paddle charges now (prorated), then confirm.
  const [planChange, setPlanChange] = useState(null); // { plan, charge_now_minor, currency, next_billed_at }
  const [changingPlan, setChangingPlan] = useState(false);
  const reloadPlans = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/quote/billing/plans`);
      setPlans(data);
    } catch { /* keep current */ }
  };
  const previewPlanChange = async (plan) => {
    setChangingPlan(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/quote/billing/paddle-change-plan`, { plan, preview: true });
      setPlanChange({ plan, ...data });
    } catch {
      toast.error(t('upgrade.planChangeError'));
    } finally {
      setChangingPlan(false);
    }
  };
  const confirmPlanChange = async () => {
    if (!planChange) return;
    setChangingPlan(true);
    try {
      await axios.post(`${API_URL}/api/quote/billing/paddle-change-plan`, { plan: planChange.plan, preview: false });
      toast.success(t('upgrade.planChanged'));
      setPlanChange(null);
      refreshEnt();
      await reloadPlans();
    } catch {
      toast.error(t('upgrade.planChangeError'));
    } finally {
      setChangingPlan(false);
    }
  };

  if (loading || entLoading || !plans) {
    return (
      <QuoteLayout>
        <div className="p-10 text-center text-[#64748B]">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> {t('upgrade.loading')}
        </div>
      </QuoteLayout>
    );
  }

  const activePlanKey = period === 'monthly' ? 'pro_monthly' : 'pro_yearly';
  const activePro = plans[activePlanKey];
  const planLang = plans.language || 'tr';
  const monthlyEquivalent = period === 'yearly'
    ? Math.round((plans.pro_yearly.amount / 12) * 100) / 100
    : plans.pro_monthly.amount;

  return (
    <QuoteLayout>
      <div className="max-w-5xl mx-auto" data-testid="quote-upgrade-page">
        <button
          onClick={() => navigate(-1)}
          className="text-sm text-[#2563EB] hover:underline flex items-center gap-1 mb-6"
          data-testid="quote-upgrade-back"
        >
          <ArrowLeft className="w-4 h-4" /> {t('upgrade.back')}
        </button>

        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-[#2563EB] text-xs font-semibold mb-3">
            <Sparkles className="w-3.5 h-3.5" /> {t('upgrade.badge')}
          </div>
          <h1 className="text-3xl font-bold text-[#1A1A2E]">{t('upgrade.heroTitle')}</h1>
          <p className="text-sm text-[#64748B] mt-2">
            {t('upgrade.heroSubtitle')}
          </p>
        </div>

        {/* Current state */}
        <div className="bg-white rounded-2xl border border-[#E5E5E5] p-5 mb-6" data-testid="quote-upgrade-current">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs text-[#94A3B8]">{t('upgrade.currentPlan')}</p>
              <p className="text-lg font-semibold flex items-center gap-2">
                {isPro ? <Crown className="w-5 h-5 text-amber-500" /> : <Sparkles className="w-4 h-4" />}
                {entitlement?.plan === 'business' ? 'Business' : isPro ? t('upgrade.planPro') : t('upgrade.planFree')}
              </p>
              {isPro && entitlement?.expires_at && (
                <p className="text-xs text-[#64748B]">
                  {t('upgrade.expiresAt', { date: String(entitlement.expires_at).slice(0, 10) })}
                </p>
              )}
            </div>
            <div className="text-right text-xs text-[#64748B]">
              <p>{t('upgrade.monthlyUsageQuotes')} <b>{entitlement?.usage?.quote_count ?? 0}</b> / {entitlement?.limits?.quote_count ?? t('upgrade.unlimited')}</p>
              <p>{t('upgrade.monthlyUsageAi')} <b>{entitlement?.usage?.ai_count ?? 0}</b> / {entitlement?.limits?.ai_count}</p>
              <p>{t('upgrade.customers')} <b>{entitlement?.usage?.customer_count ?? 0}</b> / {entitlement?.limits?.customer_limit ?? t('upgrade.unlimited')}</p>
            </div>
          </div>
        </div>

        {/* Period toggle */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex bg-slate-100 rounded-full p-1" data-testid="quote-upgrade-period">
            <button
              onClick={() => setPeriod('monthly')}
              className={`px-4 py-1.5 text-sm rounded-full font-medium ${period === 'monthly' ? 'bg-white shadow text-[#1A1A2E]' : 'text-[#64748B]'}`}
              data-testid="period-monthly"
            >
              {t('upgrade.monthly')}
            </button>
            <button
              onClick={() => setPeriod('yearly')}
              className={`px-4 py-1.5 text-sm rounded-full font-medium ${period === 'yearly' ? 'bg-white shadow text-[#1A1A2E]' : 'text-[#64748B]'}`}
              data-testid="period-yearly"
            >
              {t('upgrade.yearly')}{' '}
              <span className="text-emerald-600 text-[10px] font-bold ml-1">
                {plans?.pro_yearly?.annual_hint || t('upgrade.yearlyBadge')}
              </span>
            </button>
          </div>
        </div>

        {/* Plan cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Free */}
          <div className="bg-white border border-[#E5E5E5] rounded-2xl p-6" data-testid="plan-free-card">
            <p className="text-xs uppercase tracking-wider text-[#94A3B8] font-semibold">
              {plans.free?.name || t('upgrade.freeCardTitle')}
            </p>
            <p className="text-3xl font-bold mt-1">
              {fmtMoney(plans.free?.amount || 0, plans.free?.currency || activePro.currency, planLang)}
              <span className="text-sm text-[#94A3B8]"> {t('upgrade.perMonth')}</span>
            </p>
            <p className="text-xs text-[#64748B] mt-1">{t('upgrade.freeSubtitle')}</p>
            <ul className="text-sm text-[#475569] mt-4 space-y-1.5">
              <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> {t('upgrade.freeFeature1')}</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> {t('upgrade.freeFeature2')}</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> {t('upgrade.freeFeature3')}</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> {t('upgrade.freeFeature5')}</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" /> {t('upgrade.freeFeature4')}</li>
            </ul>
          </div>

          {/* Pro */}
          <div className="bg-gradient-to-br from-blue-600 to-purple-700 text-white rounded-2xl p-6 shadow-xl ring-2 ring-blue-300" data-testid="plan-pro-card">
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-wider font-semibold opacity-90">
                {activePro?.name || t('upgrade.proCardTitle')}
              </p>
              <Crown className="w-5 h-5 text-amber-300" />
            </div>
            <p className="text-3xl font-bold mt-1">
              {fmtMoney(activePro.amount, activePro.currency, planLang)}
              <span className="text-sm opacity-80"> {period === 'monthly' ? t('upgrade.perMonth') : t('upgrade.perYear')}</span>
            </p>
            {period === 'yearly' && (
              <p className="text-xs opacity-80 mt-0.5">
                {t('upgrade.monthlyEquivalent', { value: fmtMoney(monthlyEquivalent, activePro.currency, planLang) })}
              </p>
            )}
            <ul className="text-sm mt-4 space-y-1.5">
              <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-300 shrink-0 mt-0.5" /> {t('upgrade.proFeature1')}</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-300 shrink-0 mt-0.5" /> {t('upgrade.proFeature2')}</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-300 shrink-0 mt-0.5" /> {t('upgrade.proFeature3')}</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-300 shrink-0 mt-0.5" /> {t('upgrade.proFeature4')}</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-300 shrink-0 mt-0.5" /> {t('upgrade.proFeature6')}</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-300 shrink-0 mt-0.5" /> {t('upgrade.proFeature7')}</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-300 shrink-0 mt-0.5" /> {t('upgrade.proFeature5')}</li>
            </ul>
            {plans.provider === 'paddle' && plans.paddle_subscription_active ? (
              <>
                {/* A Paddle subscription renews by itself: another checkout
                    would create a second subscription and double-bill. */}
                {plans.paddle_subscription_status === 'past_due' && (
                  <div className="mt-5 rounded-xl bg-rose-50 p-3 text-rose-900 text-sm" data-testid="plan-pro-past-due">
                    <p className="font-semibold">{t('upgrade.pastDueTitle')}</p>
                    <p className="text-xs mt-1">{t('upgrade.pastDueBody')}</p>
                    <Button onClick={() => openPortal('payment')} disabled={openingPortal} size="sm"
                      className="mt-2 bg-rose-600 hover:bg-rose-700 text-white" data-testid="plan-pro-update-card">
                      {t('upgrade.updatePaymentMethod')}
                    </Button>
                  </div>
                )}
                {plans.paddle_current_plan?.startsWith('pro') && plans.paddle_current_plan !== activePlanKey && (
                  <Button
                    onClick={() => previewPlanChange(activePlanKey)}
                    disabled={changingPlan}
                    className="w-full mt-5 bg-emerald-400 text-[#0F172A] hover:bg-emerald-300 font-semibold disabled:opacity-70"
                    data-testid="plan-pro-switch"
                  >
                    {changingPlan && !planChange
                      ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      : null}
                    {activePlanKey === 'pro_yearly' ? t('upgrade.switchToYearly') : t('upgrade.switchToMonthly')}
                  </Button>
                )}
                {planChange && planChange.plan.startsWith('pro') && (
                  <div className="mt-3 rounded-xl bg-white/95 p-3 text-[#0F172A] text-sm" data-testid="plan-pro-switch-confirm">
                    <p>{t('upgrade.planChangeCharge', { amount: fmtMoney(planChange.charge_now_minor / 100, planChange.currency, planLang) })}</p>
                    <div className="mt-2 flex gap-2">
                      <Button size="sm" onClick={confirmPlanChange} disabled={changingPlan} className="bg-[#2563EB] hover:bg-[#1E4ED8]">
                        {changingPlan ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}{t('upgrade.planChangeConfirm')}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setPlanChange(null)} disabled={changingPlan}>
                        {t('upgrade.planChangeCancel')}
                      </Button>
                    </div>
                  </div>
                )}
                <Button
                  onClick={() => openPortal()}
                  disabled={openingPortal}
                  className="w-full mt-5 bg-white text-[#2563EB] hover:bg-slate-50 font-semibold disabled:opacity-70"
                  data-testid="plan-pro-manage"
                >
                  {openingPortal ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('upgrade.opening')}</>
                  ) : t('upgrade.manageSubscription')}
                </Button>
                <p className="text-xs opacity-90 mt-2">{t('upgrade.manageSubscriptionNote')}</p>
              </>
            ) : entitlement?.plan === 'business' ? (
              <p className="mt-5 text-sm opacity-90" data-testid="plan-pro-on-business">{t('upgrade.onBusiness')}</p>
            ) : (
              <Button
                onClick={() => buy(activePlanKey)}
                disabled={purchasing}
                className="w-full mt-5 bg-white text-[#2563EB] hover:bg-slate-50 font-semibold disabled:opacity-70"
                data-testid="plan-pro-cta"
              >
                {purchasing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('upgrade.purchasing')}</>
                ) : (
                  <>{entitlement?.plan === 'pro' ? t('upgrade.renewLabel') : t('upgrade.upgradeLabel')} — {fmtMoney(activePro.amount, activePro.currency, planLang)}</>
                )}
              </Button>
            )}
            <p className="text-[11px] opacity-80 mt-3 flex items-start gap-1.5" data-testid="plan-pro-fineprint">
              <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              {plans.provider === 'paddle' ? t('upgrade.paddleFineprint') : t('upgrade.fineprint')}
            </p>
          </div>
          <div className="bg-white border border-[#E5E5E5] rounded-2xl p-6" data-testid="plan-business-card">
            <p className="text-xs uppercase tracking-wider text-[#64748B] font-semibold">Business</p>
            <p className="text-3xl font-bold mt-1">
              {fmtMoney(plans[`business_${period}`]?.amount, activePro.currency, planLang)}
              <span className="text-sm text-[#94A3B8]"> {period === 'monthly' ? t('upgrade.perMonth') : t('upgrade.perYear')}</span>
            </p>
            <p className="text-sm text-[#475569] mt-4">{t('upgrade.businessFeatures')}</p>
            {(() => {
              const businessKey = `business_${period}`;
              const available = !!plans[businessKey]?.checkout_available;
              const current = plans.paddle_current_plan || null;
              if (!available) {
                return (
                  <>
                    <p className="text-xs text-amber-700 mt-3">{t('upgrade.businessPlanned')}</p>
                    <Button disabled className="w-full mt-5" variant="outline">{t('upgrade.comingSoon')}</Button>
                  </>
                );
              }
              if (current === businessKey || (!plans.paddle_subscription_active && entitlement?.plan === 'business' && plans.provider === 'paddle')) {
                return <p className="mt-5 text-sm font-semibold text-emerald-700" data-testid="plan-business-current">{t('upgrade.currentPlanLabel')}</p>;
              }
              if (plans.paddle_subscription_active) {
                // An existing Paddle subscription is switched (prorated), never bought twice.
                return (
                  <>
                    <Button onClick={() => previewPlanChange(businessKey)} disabled={changingPlan}
                      className="w-full mt-5 bg-[#1A1A2E] hover:bg-[#16213E] text-white" data-testid="plan-business-switch">
                      {changingPlan && !planChange ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      {t('upgrade.switchToBusiness')}
                    </Button>
                    {planChange && planChange.plan === businessKey && (
                      <div className="mt-3 rounded-xl bg-slate-50 p-3 text-sm" data-testid="plan-business-switch-confirm">
                        <p>{t('upgrade.planChangeCharge', { amount: fmtMoney(planChange.charge_now_minor / 100, planChange.currency, planLang) })}</p>
                        <div className="mt-2 flex gap-2">
                          <Button size="sm" onClick={confirmPlanChange} disabled={changingPlan} className="bg-[#2563EB] hover:bg-[#1E4ED8]">
                            {changingPlan ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}{t('upgrade.planChangeConfirm')}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setPlanChange(null)} disabled={changingPlan}>
                            {t('upgrade.planChangeCancel')}
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                );
              }
              return (
                <>
                  {entitlement?.plan === 'pro' && plans.provider !== 'paddle' && (
                    <p className="text-xs text-[#64748B] mt-3">{t('upgrade.businessFromProNote')}</p>
                  )}
                  <Button onClick={() => buy(businessKey)} disabled={purchasing}
                    className="w-full mt-5 bg-[#1A1A2E] hover:bg-[#16213E] text-white font-semibold disabled:opacity-70"
                    data-testid="plan-business-cta">
                    {purchasing ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('upgrade.purchasing')}</>
                    ) : (
                      <>{entitlement?.plan === 'business' ? t('upgrade.renewBusiness') : t('upgrade.switchToBusiness')} — {fmtMoney(plans[businessKey]?.amount, plans[businessKey]?.currency || activePro.currency, planLang)}</>
                    )}
                  </Button>
                </>
              );
            })()}
          </div>
        </div>

        {/* PayTR iframe modal */}
        {iframeUrl && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="paytr-iframe-modal">
            <div className="bg-white rounded-2xl w-full max-w-2xl h-[80vh] flex flex-col overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <p className="text-sm font-semibold">{t('upgrade.modalTitle')}</p>
                <button
                  onClick={() => { setIframeUrl(null); setPollingSession(null); }}
                  className="text-sm text-[#64748B] hover:text-[#1A1A2E]"
                  data-testid="paytr-iframe-close"
                >
                  {t('upgrade.modalClose')}
                </button>
              </div>
              <iframe
                src={iframeUrl}
                title="PayTR"
                className="flex-1 w-full"
                sandbox="allow-scripts allow-forms allow-same-origin allow-top-navigation"
                data-testid="paytr-iframe"
              />
            </div>
          </div>
        )}
      </div>
    </QuoteLayout>
  );
};

export default QuoteUpgrade;
